/* eslint-disable @next/next/no-img-element -- brand imagery is remote CMS (Cloudinary) or /public URLs that bypass the site's next/image loader; this component applies Cloudinary transforms directly instead. */
import { isCloudinaryUrl, transformCloudinaryUrl } from 'shared/types/utils/cloudinary';

interface BrandImageProps {
  src?: string;
  alt: string;
  className?: string;
  /**
   * Delivery width in px (include a DPR headroom, e.g. 2× the displayed size). When set and the src
   * is a Cloudinary URL, it's fetched at that width in an auto format (WebP/AVIF) at auto quality;
   * non-Cloudinary URLs (e.g. /public assets) are served unchanged.
   */
  width?: number;
  /** Below-the-fold images should stay lazy (the default); pass "eager" for an above-fold/LCP image. */
  loading?: 'lazy' | 'eager';
  /**
   * For studio product shots on a WHITE background: recolor the white to the brand light gray
   * (#F2F2F2) via Cloudinary so the product sits on the brand tile color. The 60-tolerance leaves
   * the product itself untouched. Only meaningful for white-cutout product images — never for
   * lifestyle photography.
   */
  grayBackground?: boolean;
  /**
   * Crop to this aspect ratio on Cloudinary (e.g. '3:4') with auto gravity, so every delivered pixel
   * lands inside an object-cover frame instead of being cropped away by the browser. Only applies to
   * Cloudinary URLs; pair with `width` (the delivered width after cropping).
   */
  aspectRatio?: string;
  /**
   * Overrides the default 'auto' subject-detection gravity used with `aspectRatio` — e.g. 'west' to
   * anchor the crop to the left edge when auto-gravity picks the wrong focal point.
   */
  gravity?: string;
  /**
   * The image's rendered width as a CSS `sizes` expression (e.g. '100vw', '(min-width: 768px) 50vw, 100vw').
   * With `width` set on a Cloudinary src, a srcset ladder capped at `width` is emitted so the browser
   * fetches only the candidate it needs. Defaults to '100vw'.
   */
  sizes?: string;
  /** Cloudinary quality (default 'auto'); use 'auto:good' or 'auto:eco' for large hero photography. */
  quality?: string;
}

// Width candidates for the srcset ladder; each image gets the rungs at or below its `width`.
const SRCSET_LADDER = [320, 480, 640, 800, 1000, 1200, 1600, 2000, 2400];

// Single place brand-refresh <img> usage gets image optimization: Cloudinary format/quality/resize
// transforms + lazy loading. Swap a plain <img> for this wherever the src can be a CMS image.
const BrandImage = ({
  src,
  alt,
  className,
  width,
  loading = 'lazy',
  grayBackground,
  aspectRatio,
  gravity,
  sizes,
  quality,
}: BrandImageProps) => {
  if (!src) return null;
  const baseOptions = {
    ...(aspectRatio ? { aspectRatio, crop: 'fill', gravity: gravity ?? 'auto' } : {}),
    ...(grayBackground ? { effects: ['e_replace_color:F2F2F2:60:FFFFFF'] } : {}),
    ...(quality ? { quality } : {}),
  };
  const optimized =
    width || grayBackground || aspectRatio || quality
      ? transformCloudinaryUrl(src, { ...baseOptions, ...(width ? { width } : {}) })
      : src;
  // Responsive candidates: only for Cloudinary URLs with a target width, and only when the ladder
  // yields more than the single width already in `src`.
  const rungs = width && isCloudinaryUrl(src) ? SRCSET_LADDER.filter((w) => w < width) : [];
  const srcSet =
    rungs.length > 0
      ? [...rungs, width as number]
          .map((w) => `${transformCloudinaryUrl(src, { ...baseOptions, width: w })} ${w}w`)
          .join(', ')
      : undefined;
  return (
    <img
      src={optimized}
      srcSet={srcSet}
      sizes={srcSet ? (sizes ?? '100vw') : undefined}
      alt={alt}
      className={className}
      loading={loading}
      fetchPriority={loading === 'eager' ? 'high' : undefined}
    />
  );
};

export default BrandImage;
